import { Grid, Theme, makeStyles } from '@material-ui/core';
import { RequestIndicatorTypes } from 'data-layer-cr/dist/models';
import {
  ContainedButtonGroup,
  DefaultCard,
  FormHookResult,
  FormValueTypes,
} from 'design-system';
import { isAlphaNumericInSpanish } from 'src/functions/isAlphaNumeric';
import { makeDateField, makeGenderRadio, makeTextField } from '../functions';

// local models
import CatalogItem from '../../../../../models/helperTypes/CatalogItem';
import {
  LEGAL_PERSON,
  NATURAL_PERSON,
} from '../../../../../models/helperTypes/CustomerItem';

// form styles
import makeFormStyles from '../../styles/makeFormStyles';

const useStyles = makeStyles((theme: Theme) => ({
  tabs: {
    marginBottom: '10px',
    // the contained button group has an internal padding that is not accessible
    // to workaround this a negative margin is added when in desktop mode
    [theme.breakpoints.up('md')]: {
      marginLeft: '-10px',
    },
  },
}));

// Description for customer type
const customerTypes = {
  [NATURAL_PERSON]: 'Persona Física',
  [LEGAL_PERSON]: 'Persona Jurídica',
};

type CustomerTypeFormProps = {
  getInputProps: FormHookResult;
  onCustomerTypeChange: (newValues: { [key: string]: FormValueTypes }) => void;
  loadingGendersState: RequestIndicatorTypes;
  mappedGenders: CatalogItem[];
};

const CustomerTypeForm = ({
  getInputProps,
  onCustomerTypeChange,
  mappedGenders,
}: CustomerTypeFormProps): JSX.Element => {
  const localClasses = useStyles();
  const classes = makeFormStyles();
  const currentCustomerType = getInputProps('type').value as string;

  // Clear all inputs on switch
  function handleCustomerTypeChange(newVal: string): void {
    onCustomerTypeChange({
      type: newVal,
      firstName: '',
      lastName: '',
      gender: '',
      birthday: '',
      email: '',
      phoneNumber: '',
      entityName: '',
      driverAndCustomerAreDifferent: newVal === LEGAL_PERSON, // Different driver when legal person is involved
      driverFirstName: '',
      driverLastName: '',
      driverBirthday: '',
      driverGender: '',
    });
  }

  return (
    <>
      <Grid item xs={12} md={6} className={localClasses.tabs}>
        <ContainedButtonGroup
          data-testid="test"
          inputLabel="Tipo de cliente"
          inputName="customer-type"
          buttons={customerTypes}
          defaultValue={getInputProps('type').value as string}
          onChange={handleCustomerTypeChange}
          useLinealStyle
        />
      </Grid>
      {/* Natural Person Customer */}
      {currentCustomerType === NATURAL_PERSON && (
        <DefaultCard data-testid="natural-section">
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} className={classes.customerInputRow}>
              {makeTextField({
                label: 'Nombre *',
                key: 'firstName',
                id: 'sales-web-input-firstname',
                getInputProps,
                shouldUpdate: isAlphaNumericInSpanish,
              })}
            </Grid>
            <Grid item xs={12} md={6} className={classes.customerInputRow}>
              {makeTextField({
                label: 'Apellido *',
                key: 'lastName',
                id: 'sales-web-input-lastname',
                getInputProps,
                shouldUpdate: isAlphaNumericInSpanish,
              })}
            </Grid>
            <Grid item xs={12} md={6} className={classes.customerInputRow}>
              {makeDateField({
                id: 'sales-web-input-birthday',
                key: 'birthday',
                label: 'Fecha de nacimiento *',
                getInputProps,
              })}
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
              className={classes.customerInputRow}
              data-testid="gender-field"
            >
              {makeGenderRadio({
                label: 'Género *',
                id: 'sales-web-radio-gender',
                getInputProps,
                key: 'gender',
                mappedGenders,
              })}
            </Grid>

            <Grid item xs={12} md={6} className={classes.customerInputRow}>
              {makeTextField({
                label: 'Correo electrónico',
                key: 'email',
                id: 'sales-web-input-email',
                getInputProps,
              })}
            </Grid>
            <Grid item xs={12} md={6} className={classes.customerInputRow}>
              {makeTextField({
                label: 'Celular',
                key: 'phoneNumber',
                id: 'sales-web-input-phonenumber',
                getInputProps,
              })}
            </Grid>
          </Grid>
        </DefaultCard>
      )}

      {/* Legal Person Customer */}
      {currentCustomerType === LEGAL_PERSON && (
        <DefaultCard data-testid="legal-section">
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              {makeTextField({
                label: 'Nombre de empresa *',
                key: 'entityName',
                getInputProps,
                personType: currentCustomerType,
              })}
            </Grid>

            <Grid item xs={12} md={6}>
              {makeTextField({
                label: 'Celular o teléfono',
                key: 'phoneNumber',
                getInputProps,
              })}
            </Grid>

            <Grid item xs={12} md={6}>
              {makeTextField({
                label: 'Correo electrónico',
                key: 'email',
                getInputProps,
              })}
            </Grid>
          </Grid>
        </DefaultCard>
      )}
    </>
  );
};

export default CustomerTypeForm;
